import { HttpErrorResponse } from '@angular/common/http';

export type ErrorClass =
  | 'csp'
  | 'cors'
  | 'auth'
  | 'forbidden'
  | 'server'
  | 'network'
  | 'unknown';

export interface ErrorClassification {
  class: ErrorClass;
  toast: string;
  logToken: string;
}

interface ClassifyHints {
  recentCspViolation?: boolean;
}

const TOAST_BY_CLASS: Record<ErrorClass, string> = {
  csp: 'Your browser blocked the request before it reached the service. Contact your administrator.',
  cors: 'The OEM connection service rejected the request from this domain. Contact support.',
  auth: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to manage OEM connections.",
  server: 'The OEM connection service returned an error. Please try again shortly.',
  network: 'The OEM connection service is unreachable. Check your internet connection.',
  unknown: 'Could not load OEM connections.',
};

const LOG_TOKEN_BY_CLASS: Record<ErrorClass, string> = {
  csp: 'oem.error.csp',
  cors: 'oem.error.cors',
  auth: 'oem.error.auth',
  forbidden: 'oem.error.forbidden',
  server: 'oem.error.server',
  network: 'oem.error.network',
  unknown: 'oem.error.unknown',
};

export function classifyOemHttpError(
  err: unknown,
  hints?: ClassifyHints,
): ErrorClassification {
  const cls = classify(err, hints);
  return {
    class: cls,
    toast: TOAST_BY_CLASS[cls],
    logToken: LOG_TOKEN_BY_CLASS[cls],
  };
}

function classify(err: unknown, hints?: ClassifyHints): ErrorClass {
  if (!(err instanceof HttpErrorResponse)) return 'unknown';

  const status = err.status;

  if (status === 0) {
    if (hints?.recentCspViolation) return 'csp';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'network';
    }
    return 'cors';
  }

  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status >= 500 && status <= 599) return 'server';

  return 'unknown';
}
